/**
 * Read-Only Mode - For viewing executed documents
 *
 * Features:
 * - Search with highlighting
 * - View annotations (no editing)
 * - Zoom and navigation
 * - Thumbnail sidebar
 *
 * Use case: Executed/signed documents that are view-only
 */

import { useState, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { PDFPageInfo, AnnotationData } from '../types';

export interface SearchMatch {
  pageNumber: number;
  rects: Array<{ x: number; y: number; width: number; height: number }>;
}

export interface UseReadOnlyModeOptions {
  pdfDocument: pdfjsLib.PDFDocumentProxy | null;
  pagesInfo: PDFPageInfo[];
  annotations: AnnotationData[];
  onAnnotationClick?: (annotation: AnnotationData) => void;
}

export interface UseReadOnlyModeResult {
  // Search
  searchMatches: SearchMatch[];
  currentSearchResultIndex: number;
  searchQuery: string;
  handleSearch: (query: string) => Promise<void>;
  nextSearchResult: () => void;
  previousSearchResult: () => void;

  // Annotations (read-only)
  visibleAnnotations: AnnotationData[];
  handleAnnotationClick: (annotation: AnnotationData) => void;
}

/**
 * Hook for read-only mode behavior
 */
export const useReadOnlyMode = ({
  pdfDocument,
  pagesInfo,
  annotations,
  onAnnotationClick,
}: UseReadOnlyModeOptions): UseReadOnlyModeResult => {
  const [searchMatches, setSearchMatches] = useState<SearchMatch[]>([]);
  const [currentSearchResultIndex, setCurrentSearchResultIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  /**
   * Search for text in PDF and get positions for highlighting
   */
  const handleSearch = useCallback(async (query: string) => {
    setSearchQuery(query);

    if (!pdfDocument || !query.trim() || pagesInfo.length === 0) {
      setSearchMatches([]);
      setCurrentSearchResultIndex(0);
      return;
    }

    const matches: SearchMatch[] = [];
    const normalizedQuery = query.toLowerCase();

    // Search through all pages
    for (let pageNum = 1; pageNum <= pdfDocument.numPages; pageNum++) {
      const page = await pdfDocument.getPage(pageNum);
      const textContent = await page.getTextContent();

      // Use same scale as rendered canvas
      const pageInfo = pagesInfo.find(p => p.pageNumber === pageNum);
      const renderScale = pageInfo?.scale || 1;
      const viewport = page.getViewport({ scale: renderScale });

      // Build text and track character positions
      let pageText = '';
      const charToItem: any[] = [];

      textContent.items.forEach((item: any) => {
        const itemText = item.str;
        for (let i = 0; i < itemText.length; i++) {
          charToItem.push(item);
          pageText += itemText[i];
        }
        charToItem.push(null);
        pageText += ' ';
      });

      const pageTextLower = pageText.toLowerCase();

      // Find all occurrences and get their positions
      let searchIndex = 0;
      while (true) {
        const index = pageTextLower.indexOf(normalizedQuery, searchIndex);
        if (index === -1) break;

        const rects: Array<{ x: number; y: number; width: number; height: number }> = [];
        const endIndex = index + normalizedQuery.length;

        let currentItem: any = null;
        let currentRect: any = null;

        for (let i = index; i < endIndex && i < charToItem.length; i++) {
          const item = charToItem[i];
          if (!item) continue;

          if (item !== currentItem) {
            if (currentRect) {
              rects.push(currentRect);
            }

            // Count how many consecutive characters from this item are in the match
            let charCount = 0;
            let startCharInItem = 0;

            // Find where in the item string this match starts
            // We need to count how many chars from this item came before index i
            let charsBeforeMatch = 0;
            for (let k = 0; k < i && k < charToItem.length; k++) {
              if (charToItem[k] === item) {
                charsBeforeMatch++;
              }
            }
            startCharInItem = charsBeforeMatch;

            // Count consecutive characters from this item in the match
            let j = i;
            while (j < endIndex && j < charToItem.length && charToItem[j] === item) {
              charCount++;
              j++;
            }

            // Convert item coordinates to viewport space using PDF.js utilities
            const transform = item.transform;

            // Get item start and end positions in PDF space
            const itemStartX = transform[4];
            const itemStartY = transform[5];

            // Convert to viewport coordinates
            const [vpX0, vpY0] = viewport.convertToViewportPoint(itemStartX, itemStartY);
            const [vpX1] = viewport.convertToViewportPoint(itemStartX + item.width, itemStartY);

            // Calculate width in viewport space (no double-scaling)
            const itemWidthInViewport = vpX1 - vpX0;
            const charWidthInViewport = itemWidthInViewport / item.str.length;

            // Calculate match position and width
            const matchStartX = vpX0 + (startCharInItem * charWidthInViewport);
            const matchWidth = charCount * charWidthInViewport;

            // Calculate height using viewport transform
            const tx = (pdfjsLib as any).Util.transform(viewport.transform, transform);
            const fontHeightVp = Math.sqrt(tx[2] * tx[2] + tx[3] * tx[3]);

            currentItem = item;
            currentRect = {
              x: matchStartX,
              y: vpY0 - fontHeightVp,
              width: matchWidth,
              height: fontHeightVp * 1.2,
            };

          }
        }

        if (currentRect) {
          rects.push(currentRect);
        }

        if (rects.length > 0) {
          matches.push({ pageNumber: pageNum, rects });
        }

        searchIndex = index + 1;
      }
    }

    setSearchMatches(matches);
    setCurrentSearchResultIndex(matches.length > 0 ? 0 : -1);
  }, [pdfDocument, pagesInfo]);

  /**
   * Navigate to next search result
   */
  const nextSearchResult = useCallback(() => {
    if (searchMatches.length === 0) return;

    const nextIndex = currentSearchResultIndex >= searchMatches.length - 1
      ? 0
      : currentSearchResultIndex + 1;
    setCurrentSearchResultIndex(nextIndex);

    // Scroll to result page using manual scroll (prevents layout shift)
    const match = searchMatches[nextIndex];
    const pageElement = document.getElementById(`page-wrapper-${match.pageNumber}`);
    if (pageElement) {
      // Find scrollable container (pagesContainer)
      const container = pageElement.parentElement;
      if (container) {
        const containerRect = container.getBoundingClientRect();
        const pageRect = pageElement.getBoundingClientRect();
        const offset = pageRect.top - containerRect.top + container.scrollTop;
        // Center the page in the viewport
        const centerOffset = offset - (containerRect.height - pageRect.height) / 2;
        container.scrollTop = centerOffset;
      }
    }
  }, [searchMatches, currentSearchResultIndex]);

  /**
   * Navigate to previous search result
   */
  const previousSearchResult = useCallback(() => {
    if (searchMatches.length === 0) return;

    const prevIndex = currentSearchResultIndex <= 0
      ? searchMatches.length - 1
      : currentSearchResultIndex - 1;
    setCurrentSearchResultIndex(prevIndex);

    // Scroll to result page using manual scroll (prevents layout shift)
    const match = searchMatches[prevIndex];
    const pageElement = document.getElementById(`page-wrapper-${match.pageNumber}`);
    if (pageElement) {
      // Find scrollable container (pagesContainer)
      const container = pageElement.parentElement;
      if (container) {
        const containerRect = container.getBoundingClientRect();
        const pageRect = pageElement.getBoundingClientRect();
        const offset = pageRect.top - containerRect.top + container.scrollTop;
        // Center the page in the viewport
        const centerOffset = offset - (containerRect.height - pageRect.height) / 2;
        container.scrollTop = centerOffset;
      }
    }
  }, [searchMatches, currentSearchResultIndex]);

  /**
   * Handle annotation click (read-only, just notify parent)
   */
  const handleAnnotationClick = useCallback((annotation: AnnotationData) => {
    onAnnotationClick?.(annotation);
  }, [onAnnotationClick]);

  return {
    // Search
    searchMatches,
    currentSearchResultIndex,
    searchQuery,
    handleSearch,
    nextSearchResult,
    previousSearchResult,

    // Annotations
    visibleAnnotations: annotations,
    handleAnnotationClick,
  };
};
